import type { ReactNode } from 'react'
import { Navigate, NavLink, Outlet, useLocation } from 'react-router'
import { useAuth } from '../app/auth-context'
import { ROLE_LABEL } from '../lib/staff-api'
import { Button } from './ui'

const navClass = ({ isActive }: { isActive: boolean }) =>
  `block rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
    isActive ? 'bg-brand-100 text-brand-800 dark:bg-brand-900 dark:text-brand-100' : 'text-strong hover:bg-sunken'
  }`

/** Signed-in frame: sidebar on the right (RTL), page on the left. Sends anyone else to /login. */
export function Shell() {
  const { status, staff, signOut } = useAuth()
  const location = useLocation()

  if (status === 'loading') {
    return <div className="grid min-h-dvh place-items-center text-muted">در حال بارگذاری…</div>
  }
  if (!staff) return <Navigate to="/login" replace state={{ from: location.pathname }} />

  return (
    <div className="flex min-h-dvh">
      <aside className="flex w-60 shrink-0 flex-col border-l border-line bg-card p-4">
        <div className="mb-6 px-3">
          <div className="text-lg font-bold">بیمه گلد</div>
          <div className="text-xs text-muted">پیشخوان</div>
        </div>

        <nav className="flex flex-col gap-1">
          <NavLink to="/" end className={navClass}>
            خانه
          </NavLink>
          <NavLink to="/customers" className={navClass}>
            مشتریان
          </NavLink>
          <NavLink to="/orders" className={navClass}>
            سفارش‌ها
          </NavLink>
          <NavLink to="/payments" className={navClass}>
            پرداخت‌ها
          </NavLink>
          {staff.role === 'ADMIN' ? (
            <>
              <NavLink to="/staff" className={navClass}>
                کارکنان
              </NavLink>
              <NavLink to="/audit" className={navClass}>
                رویدادها
              </NavLink>
            </>
          ) : null}
        </nav>

        <div className="mt-auto border-t border-line pt-4">
          <div className="px-3 text-sm font-semibold">{staff.name}</div>
          <div className="mb-3 px-3 text-xs text-muted">{ROLE_LABEL[staff.role]}</div>
          <Button variant="secondary" className="w-full" onClick={() => void signOut()}>
            خروج
          </Button>
        </div>
      </aside>

      <main className="min-w-0 flex-1 p-8">
        <Outlet />
      </main>
    </div>
  )
}

/** Wraps admin-only pages. The API refuses anyway; this just avoids showing a dead page. */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { staff } = useAuth()
  if (staff?.role !== 'ADMIN') return <Navigate to="/" replace />
  return <>{children}</>
}
